import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from '@emotion/styled';
import moment from 'moment';
import { toast } from 'react-toastify';
import { suppliesStatusOptions, typeOfSelectionByDateOptions } from '../../../helpers';

import PageTitle from '../../common/PageTitle';
import Checkbox from '../../common/Checkbox';
import Button from '../../common/Button';
import SelectComponent from '../../common/Select';
import DatePicker from '../../common/DatePicker';

import { fetchGetSuppliesReport, fetchGetSuppliesCount } from '../../../actions/supplies';

import { ORDERS_EXPORT_PAGE_LABELS, SELECTION_BY_DATE_TRANSLATE } from '../../../config';
import { fetchGetCounteragents } from '../../../actions/settings';
import { useAutoTranslation } from '../../../helpers/useAutoTranslation';

const Wrapper = styled(`div`)`
  .parameters-wrapper {
    display: flex;
    margin-bottom: 30px;
  }

  .divider-wrapper {
    margin: auto 10px 10px;
    font-weight: bold;
  }

  .parameter-wrapper {
    flex-basis: 23.375%;
    max-width: 235.885px;

    label {
      display: block;
    }

    &--wide {
      flex-basis: 50%;
      max-width: 500px;
      min-width: 210px;
    }

    .form-control {
      min-height: 38px;
    }

    .react-datepicker-wrapper {
      width: 100%;
      height: 38px;

      .react-datepicker__input-container {
        height: 100%;

        input {
          height: 100%;
        }
      }
    }
  }

  .options-wrapper {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 10px;

    .custom-control-label {
      margin-right: 45px;
      margin-bottom: 15px;
    }
  }

  .buttons-wrapper {
    padding-top: 24px;
    position: relative;
    display: flex;

    &::before {
      content: '';
      position: absolute;
      left: -24px;
      top: 0;
      width: calc(100% + 48px);
      border-bottom: 1px solid #f1f5f8;
    }
  }

  .counter-title {
    margin: 3px 0 11px;
    font-size: 11px;
    color: #98a6ad;
    font-weight: 600;
  }

  ul {
    padding-left: 15px;
  }
`;

const NewSuppliesExportPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { t } = useAutoTranslation();

  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [exportItems, setExportItems] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loadingReport, setLoadingReport] = useState(false);
  const [result, setResult] = useState();
  const [count, setCount] = useState();
  const [typeOfSelectionByDate, setTypeOfSelectionByDate] = useState(
    typeOfSelectionByDateOptions[0],
  );

  useEffect(() => {
    setResult(undefined);

    const arrOfStatuses = statuses.map((status) => status.value);
    const arrOfSuppliers = suppliers.map((supplier) => supplier.value);

    const body = {
      exportItems: exportItems,
      statuses: arrOfStatuses,
      suppliers: arrOfSuppliers,
      fromDate: moment(fromDate).format(`YYYY-MM-DD HH:mm:ss`),
      toDate: moment(toDate).format(`YYYY-MM-DD HH:mm:ss`),
      ids: [],
    };

    dispatch(fetchGetSuppliesCount(body)).then(({ count, limit }) => {
      setCount(count);
      if (count > limit) toast.warn(`!`);
    });
  }, [fromDate, toDate, exportItems, statuses, suppliers]);

  const [suppliersOptions, setSuppliersOptions] = useState();

  useEffect(() => {
    dispatch(fetchGetCounteragents({ size: 1000 })).then((result) => {
      const suppliersObj = result.counteragents
        .filter((agent) => agent.name !== 'Розничный покупатель')
        .map((agent) => ({ value: agent.id, label: agent.name }));
      setSuppliersOptions(suppliersObj);
    });
  }, [dispatch]);

  const handleGetReport = async () => {
    setLoadingReport(true);

    const arrOfStatuses = statuses.map((status) => status.value);
    const arrOfSuppliers = suppliers.map((supplier) => supplier.value);

    const body = {
      exportItems: exportItems,
      statuses: arrOfStatuses,
      suppliers: arrOfSuppliers,
      typeOfSelectionByDate: typeOfSelectionByDate.value,
      fromDate: moment(fromDate).format(`YYYY-MM-DD HH:mm:ss`),
      toDate: moment(toDate).format(`YYYY-MM-DD HH:mm:ss`),
      ids: [],
    };

    try {
      await dispatch(fetchGetSuppliesReport(body));
      history.push('/supplies/export');
      setLoadingReport(false);
    } catch {
      setLoadingReport(false);
    }
  };

  return (
    <>
      <PageTitle title={t('Экспорт поставок')} />
      <Wrapper className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <p className="card-subtitle">{t('Настройки отбора поставок')}</p>
              <p className="counter-title">
                {t('Поставок в выборке')}: {count}
              </p>
              <div className="parameters-wrapper">
                <div className="parameter-wrapper">
                  <label className="">{t('Дата от')}</label>
                  <DatePicker
                    showTimeSelect
                    selected={fromDate}
                    onChange={(date) => setFromDate(date)}
                    timeFormat="p"
                    dateFormat="Pp"
                  />
                </div>
                <div className="divider-wrapper">–</div>
                <div className="parameter-wrapper">
                  <label className="">{t('Дата по')}</label>
                  <DatePicker
                    showTimeSelect
                    selected={toDate}
                    onChange={(date) => setToDate(date)}
                    timeFormat="p"
                    dateFormat="Pp"
                  />
                </div>
              </div>
              <p className="block-subtitle">{t('Дополнительные настройки')}</p>
              <div className="parameters-wrapper mb-3">
                <div className="parameter-wrapper parameter-wrapper--select parameter-wrapper--wide">
                  <SelectComponent
                    isMulti
                    label={t('Статус поставки')}
                    onChange={(value) => setStatuses(value || [])}
                    value={statuses}
                    options={suppliesStatusOptions}
                  />
                </div>
                <div className="parameter-wrapper parameter-wrapper--select parameter-wrapper--wide ml-3">
                  <SelectComponent
                    isMulti
                    label={t('Поставщик')}
                    onChange={(value) => setSuppliers(value || [])}
                    value={suppliers}
                    options={suppliersOptions}
                  />
                </div>
              </div>
              <div className="parameters-wrapper mb-3">
                <div className="parameter-wrapper parameter-wrapper--select parameter-wrapper--wide">
                  <SelectComponent
                    label={t('Тип даты')}
                    onChange={(value) => setTypeOfSelectionByDate(value || [])}
                    value={typeOfSelectionByDateOptions
                      .map((item) => ({
                        value: item.value,
                        label: t(item.label),
                      }))
                      .find((o) => o.value === typeOfSelectionByDate.value)}
                    options={typeOfSelectionByDateOptions.map((item) => ({
                      value: item.value,
                      label: t(item.label),
                    }))}
                  />
                </div>
              </div>
              <div className="options-wrapper">
                <Checkbox
                  label={t(ORDERS_EXPORT_PAGE_LABELS.exportItems)}
                  id="export-exportItems"
                  checked={exportItems}
                  onChange={(evt) => setExportItems(evt.target.checked)}
                />
              </div>
              <div className="buttons-wrapper">
                <Button
                  text={t('Начать экспорт')}
                  className="mr-3"
                  loading={loadingReport}
                  onClick={handleGetReport}
                />
                {result ? (
                  <div>
                    {t('Файл сформирован')}:{' '}
                    <a href={result.fileUrl} target="_blank" rel="noopener noreferrer">
                      {result.fileID}
                    </a>
                    <br />
                    {t('Дата выгрузки')}: {result.finishTime}, {t('размер файла')}:{' '}
                    {result.fileSize}
                  </div>
                ) : (
                  loadingReport && (
                    <div>
                      {t('Формирование файла с товарами')} –<br />
                      {t('пожалуйста, дождитесь окончания операции')}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </Wrapper>
    </>
  );
};

export default NewSuppliesExportPage;
